/**
 * لایه ترسیم روی نمودار
 * ======================
 * ابزارهای تحلیل‌گر: خط افقی و عمودی، خط روند، نیم‌خط، خط بی‌نهایت، کانال
 * موازی، ناحیه، فیبوناچی، پیکان، قلم آزاد، خط‌کش اندازه‌گیری و یادداشت.
 *
 * ── چرا یک لایه SVG جدا و نه امکانات خود کتابخانه ──────────────────────
 * `lightweight-charts` عمداً سبک است و **هیچ ابزار ترسیمی ندارد**. تنها
 * چیزی که می‌دهد تبدیل مختصات است: «این قیمت روی کدام پیکسل است؟». همین
 * کافی است تا یک لایه SVG دقیقاً روی نمودار بنشیند و با هر جابه‌جایی و
 * بزرگ‌نمایی همراهش حرکت کند.
 *
 * ── چرا زمان ذخیره می‌شود و نه شماره کندل ──────────────────────────────
 * شماره کندل (logical index) با هر بار تازه‌شدن داده جابه‌جا می‌شود: کندل
 * تازه که می‌آید، همه شماره‌ها یکی می‌لغزند و خط‌های کاربر کج می‌شوند.
 * زمان اما ثابت است؛ خطی که روی «سه‌شنبه ساعت ۴» کشیده شده، همان‌جا
 * می‌ماند.
 *
 * ── نکته مهم درباره رویدادها ──────────────────────────────────────────
 * لایه به‌طور پیش‌فرض `pointer-events: none` است تا جابه‌جایی و بزرگ‌نمایی
 * نمودار مثل قبل کار کند. فقط وقتی ابزاری فعال است، یا وقتی نشانگر روی
 * خودِ یک ترسیم می‌رود، رویداد می‌گیرد.
 *
 * ── لمسی ──────────────────────────────────────────────────────────────
 * همه‌جا `pointer`‌ رویداد استفاده شده، نه `mouse`. با رویدادهای ماوس،
 * ترسیم روی گوشی اصلاً کار نمی‌کرد: انگشت رویداد `mousedown` نمی‌سازد.
 */
import {
  memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react'
import type { IChartApi, ISeriesApi } from 'lightweight-charts'
import type { Candle } from '../lib/indicators'
import { useMediaQuery } from '../lib/media'
import {
  barStep, logicalFromTime, nearestBarIndex, simplifyIndices, timeFromLogical,
} from '../lib/chartCoords'

export type Tool =
  | 'none' | 'hline' | 'vline' | 'trend' | 'ray' | 'ext' | 'channel'
  | 'rect' | 'fib' | 'arrow' | 'brush' | 'measure' | 'text'

export type Point = { t: number; p: number }
export type Drawing = {
  id: string
  kind: Exclude<Tool, 'none'>
  pts: Point[]
  color: string
  text?: string
}

/** چند نقطه برای کامل‌شدن هر ابزار لازم است */
export const TOOL_POINTS: Record<Exclude<Tool, 'none'>, number> = {
  hline: 1, vline: 1, text: 1,
  trend: 2, ray: 2, ext: 2, rect: 2, fib: 2, arrow: 2, measure: 2, brush: 2,
  channel: 3,
}

export const FIB_LEVELS = [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1]

/** ابزارهایی که با کشیدن انگشت/ماوس کشیده می‌شوند، نه با کلیک‌های پیاپی */
const FREEHAND: Tool[] = ['brush']
const MAX_BRUSH_POINTS = 1500

/**
 * پالایش ترسیم‌هایی که از سرور یا حافظه مرورگر می‌آیند.
 *
 * چرا لازم است: این داده می‌تواند از نسخه قبلی برنامه مانده باشد، نصفه ذخیره
 * شده باشد، یا کاربر مستقیم دستکاری‌اش کرده باشد. یک ترسیمِ بدون نقطه کافی
 * است تا هنگام رسم `pts[0]` خطا بدهد و **کل صفحه معامله سفید شود**. پس هر
 * چیزی که شکلش درست نیست، همین‌جا کنار گذاشته می‌شود.
 */
export function sanitizeDrawings(raw: unknown): Drawing[] {
  if (!Array.isArray(raw)) return []
  const out: Drawing[] = []
  for (const d of raw as any[]) {
    if (!d || typeof d !== 'object') continue
    const kind = d.kind as Exclude<Tool, 'none'>
    const need = TOOL_POINTS[kind]
    if (!need) continue
    if (!Array.isArray(d.pts) || d.pts.length < need) continue
    // قلم آزاد نقطه‌های زیادی دارد؛ بقیه ابزارها دقیقاً به تعداد لازم.
    const src = kind === 'brush' ? d.pts.slice(0, MAX_BRUSH_POINTS) : d.pts.slice(0, need)
    const pts = src.map((q: any) => ({ t: Number(q?.t), p: Number(q?.p) }))
    if (pts.some((q: Point) => !Number.isFinite(q.t) || !Number.isFinite(q.p))) continue
    out.push({
      id: typeof d.id === 'string' && d.id ? d.id.slice(0, 40) : uid(),
      kind,
      pts,
      // رنگ مستقیم داخل ویژگی SVG می‌نشیند؛ فقط شکل هگز پذیرفته می‌شود.
      color: /^#[0-9a-fA-F]{3,8}$/.test(d.color) ? d.color : '#F5C24C',
      ...(typeof d.text === 'string' ? { text: d.text.slice(0, 120) } : {}),
    })
  }
  return out
}

type Props = {
  chart: IChartApi | null
  series: ISeriesApi<any> | null
  /** کندل‌های روی نمودار — برای جای‌گذاری دقیق و برای آهن‌ربا */
  bars: Candle[]
  tool: Tool
  color: string
  items: Drawing[]
  selected: string | null
  /** آهن‌ربا: نقطه به نزدیک‌ترین قیمتِ کندل می‌چسبد */
  magnet?: boolean
  /** قفل: ترسیم‌ها دیده می‌شوند ولی جابه‌جا نمی‌شوند */
  locked?: boolean
  /** پنهان: ترسیم‌ها موقتاً نشان داده نمی‌شوند */
  hidden?: boolean
  onSelect: (id: string | null) => void
  onCommit: (d: Drawing) => void
  onUpdate: (d: Drawing) => void
  onToolDone: () => void
}

const uid = () => `d${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`

function DrawLayerImpl({
  chart, series, bars, tool, color, items, selected,
  magnet = false, locked = false, hidden = false,
  onSelect, onCommit, onUpdate, onToolDone,
}: Props) {
  const host = useRef<HTMLDivElement>(null)
  // این شمارنده **باید** در وابستگی‌های `useMemo` رسم بیاید.
  //
  // اگر مقدارش را دور بریزیم (`const [, force]`)، با هر جابه‌جایی نمودار
  // کامپوننت دوباره رندر می‌شود ولی چون هیچ‌کدام از وابستگی‌های memo عوض
  // نشده، همان SVG قبلی برمی‌گردد: نمودار زیر پای ترسیم‌ها حرکت می‌کند و
  // خط‌ها سر جای قبلی‌شان یخ می‌زنند.
  const [tick, force] = useState(0)
  const redraw = useCallback(() => force((v) => v + 1), [])
  const [draft, setDraft] = useState<Point[]>([])
  const [ghost, setGhost] = useState<Point | null>(null)
  const drag = useRef<{ id: string; idx: number | 'all'; from: Point[]; at: Point } | null>(null)
  const [dragging, setDragging] = useState(false)
  const freehand = useRef<Point[] | null>(null)
  // آخرین مکانِ نشانگر به پیکسل — برای اینکه نقطه‌های تکراری ذخیره نشوند
  const lastPx = useRef<{ x: number; y: number } | null>(null)
  // شروعِ «کشیدن برای ترسیم» — همان رفتاری که کاربر از تریدینگ‌ویو انتظار دارد
  const press = useRef<{ pt: Point; x: number; y: number } | null>(null)
  const suppressClick = useRef(false)

  // روی صفحه لمسی، هدفِ ۸ پیکسلی عملاً گرفتنی نیست: انگشت حدود ۹ میلی‌متر
  // پهنا دارد. ولی روی ماوس، نوارِ پهن جلوی جابه‌جایی نمودار را می‌گیرد.
  const coarse = useMediaQuery('(pointer: coarse)')
  const times = useMemo(() => bars.map((b) => b.time), [bars])
  const has = items.length > 0 || draft.length > 0

  /* هر جابه‌جایی یا تغییر اندازه نمودار ⇒ ترسیم‌ها باید دوباره جای‌گذاری شوند */
  useEffect(() => {
    if (!chart) return
    const ts = chart.timeScale()
    ts.subscribeVisibleLogicalRangeChange(redraw)
    const ro = new ResizeObserver(redraw)
    if (host.current?.parentElement) ro.observe(host.current.parentElement)
    // پوشش تغییرهایی که رویداد ندارند (مثل کشیدن محور قیمت). وقتی هیچ
    // ترسیمی روی نمودار نیست، این تایمر هم بی‌مصرف است.
    const id = has ? setInterval(redraw, 1000) : 0
    return () => {
      ts.unsubscribeVisibleLogicalRangeChange(redraw)
      ro.disconnect()
      if (id) clearInterval(id)
    }
  }, [chart, redraw, has])

  /* کلیک روی فضای خالیِ نمودار ⇒ از حالت انتخاب بیرون بیا.
   *
   * چرا از خود نمودار: وقتی ابزاری فعال نیست لایه `pointer-events: none`
   * است و کلیکِ فضای خالی اصلاً به آن نمی‌رسد؛ مستقیم به بوم نمودار می‌رود.
   * پس همان‌جا شنیده می‌شود. کلیک روی خودِ یک ترسیم به بوم نمی‌رسد، چون
   * شکل‌ها رویداد را می‌گیرند. */
  useEffect(() => {
    if (!chart || tool !== 'none') return
    const h = () => onSelect(null)
    chart.subscribeClick(h)
    return () => { try { chart.unsubscribeClick(h) } catch { /* بسته شده */ } }
  }, [chart, tool, onSelect])

  /* ── تبدیل مختصات
   *
   * دو تله اینجا هست که هر دو یک بار ما را زمین زده‌اند:
   *
   * ۱. **`coordinateToTime` گرد می‌کند.** مختصات را به زمانِ نزدیک‌ترین
   *    کندل تبدیل می‌کند، نه به زمانِ دقیقِ زیر نشانگر. نتیجه‌اش این بود که
   *    قلم آزاد به‌جای خطِ نرم، یک نردبان پله‌ای می‌کشید و هر نقطه‌ای که
   *    می‌گذاشتی روی ستون کندل می‌پرید. حالا از شماره منطقیِ **اعشاری**
   *    استفاده می‌شود و زمان بین دو کندل درون‌یابی می‌شود.
   *
   * ۲. **`timeToCoordinate` فقط زمانِ روی کندل را می‌شناسد.** خطی که روی
   *    نمودار یک‌ساعته کشیده شده، روی نمودار روزانه روی هیچ کندلی نمی‌افتد
   *    و ناپدید می‌شد. مسیر منطقی این مشکل را هم ندارد.
   *
   * ذخیره‌سازی همچنان بر حسب **زمان** است (نه شماره کندل)، چون شماره‌ها با
   * آمدن هر کندل تازه می‌لغزند.
   */
  const step = useMemo(() => barStep(times), [times])

  // فاصله واقعی دو میله روی صفحه — فقط برای مسیر پشتیبان.
  //
  // `options().barSpacing` به‌درد نمی‌خورد: بزرگ‌نمایی کاربر مقدار داخلی را
  // عوض می‌کند ولی در تنظیمات نمی‌نویسد، پس همیشه همان عدد پیش‌فرض
  // برمی‌گردد. اختلاف مختصاتِ دو نقطه منطقیِ پشت‌سرهم، عدد درست را می‌دهد.
  const barSpacing = useCallback((): number => {
    try {
      const ts = chart?.timeScale()
      const a = ts?.logicalToCoordinate(0 as any)
      const b = ts?.logicalToCoordinate(1 as any)
      if (a != null && b != null) {
        const d = Math.abs((b as number) - (a as number))
        if (d > 0.01) return d
      }
    } catch { /* پایین‌تر */ }
    return 6
  }, [chart])

  /**
   * دو نقطه لنگر روی محور زمان: مختصاتِ شماره منطقیِ صفر و یک.
   *
   * با همین دو عدد هم می‌شود پیکسل را به شماره اعشاری تبدیل کرد و هم
   * برعکس — و این تنها راهِ **پیوسته** است. `coordinateToLogical` خودِ
   * کتابخانه عدد را گرد می‌کند (همان چیزی که قلم آزاد را پله‌ای می‌کرد)، پس
   * از آن استفاده نمی‌کنیم.
   */
  const anchors = useCallback((): { c0: number; per: number } | null => {
    try {
      const ts = chart?.timeScale()
      const a = ts?.logicalToCoordinate(0 as any)
      const b = ts?.logicalToCoordinate(1 as any)
      if (a == null || b == null) return null
      const per = (b as number) - (a as number)
      if (!Number.isFinite(per) || Math.abs(per) < 0.01) return null
      return { c0: a as number, per }
    } catch { return null }
  }, [chart])

  const x = useCallback((t: number): number | null => {
    const ts = chart?.timeScale()
    if (!ts || !times.length) return null
    const lg = logicalFromTime(times, t, step)
    if (lg != null) {
      // از همان دو لنگرِ `fromEvent` استفاده می‌شود تا رفت و برگشت **دقیقاً**
      // وارون هم باشند؛ وگرنه نقطه‌ای که کاربر می‌گذارد، لحظه رها کردن
      // چند پیکسل می‌پرید.
      const an = anchors()
      if (an) return an.c0 + lg * an.per
      try {
        const c = ts.logicalToCoordinate(lg as any)
        if (c != null) return c as number
      } catch { /* مسیر پشتیبان */ }
    }
    // پشتیبان: اگر نسخه کتابخانه این تبدیل را نداشت
    const v = ts.timeToCoordinate(t as any)
    if (v != null) return v as number
    if (!step) return null
    const i = nearestBarIndex(times, t)
    const c0 = ts.timeToCoordinate(times[i] as any)
    if (c0 == null) return null
    return (c0 as number) + ((t - times[i]) / step) * barSpacing()
  }, [chart, times, step, barSpacing, anchors])

  const y = useCallback((p: number): number | null => {
    const v = series?.priceToCoordinate(p)
    return v == null ? null : (v as number)
  }, [series])

  /**
   * آهن‌ربا: نقطه را به نزدیک‌ترین قیمتِ همان کندل می‌چسباند (باز، سقف، کف،
   * بسته). فاصله در **پیکسل** سنجیده می‌شود، نه در قیمت — وگرنه روی بازار
   * تومانی با اعداد میلیونی، هیچ‌وقت نمی‌چسبید و روی بازار تتری همیشه.
   */
  const snap = useCallback((pt: Point): Point => {
    if (!magnet || !bars.length) return pt
    const i = nearestBarIndex(times, pt.t)
    if (i < 0) return pt
    const b = bars[i]
    const py = y(pt.p)
    if (py == null) return pt
    let best: number | null = null
    let bestD = 14                                   // آستانه چسبیدن، به پیکسل
    for (const cand of [b.open, b.high, b.low, b.close]) {
      const cy = y(cand)
      if (cy == null) continue
      const d = Math.abs(cy - py)
      if (d < bestD) { bestD = d; best = cand }
    }
    return best == null ? { t: b.time, p: pt.p } : { t: b.time, p: best }
  }, [magnet, bars, times, y])


  const fromEvent = useCallback((e: { clientX: number; clientY: number }): Point | null => {
    const box = host.current?.getBoundingClientRect()
    if (!box || !chart || !series) return null
    const px = e.clientX - box.left
    const py = e.clientY - box.top
    const p = series.coordinateToPrice(py)
    if (p == null) return null

    const an = anchors()
    if (an && times.length) {
      const lg = (px - an.c0) / an.per
      const t = timeFromLogical(times, lg, step)
      if (t != null) return snap({ t, p: Number(p) })
    }

    // پشتیبان: مسیر قدیمی (روی ستون کندل گرد می‌شود)
    const ts = chart.timeScale()
    const raw = ts.coordinateToTime(px)
    if (raw != null) return snap({ t: Number(raw), p: Number(p) })
    if (!times.length || !step) return null
    const lastT = times[times.length - 1]
    const lastX = ts.timeToCoordinate(lastT as any)
    if (lastX == null) return null
    return { t: lastT + ((px - (lastX as number)) / barSpacing()) * step, p: Number(p) }
  }, [chart, series, times, step, barSpacing, snap, anchors])

  // اندازه **ناحیه نمودار**، نه کل کادر.
  //
  // کادر نمودار، محورِ قیمت (سمت راست) و محورِ زمان (پایین) را هم در خود
  // دارد. اگر لایه به اندازه کل کادر کشیده شود، خط افقی کاربر روی محور قیمت
  // هم می‌افتد و برچسب‌ها را می‌پوشاند. `paneSize` دقیقاً ناحیه خودِ نمودار
  // را می‌دهد؛ اگر نسخه کتابخانه نداشت، به اندازه کادر برمی‌گردیم.
  const size = () => {
    const b = host.current?.getBoundingClientRect()
    const ps = (chart as any)?.paneSize?.()
    return {
      w: (ps?.width as number) || b?.width || 0,
      h: (ps?.height as number) || b?.height || 0,
    }
  }

  /** پاک‌کردن هر حالتِ نیمه‌کاره — یک جا، تا هیچ‌کدام جا نماند */
  const resetDraft = useCallback(() => {
    setDraft((v) => (v.length ? [] : v))
    setGhost(null)
    freehand.current = null
    press.current = null
    lastPx.current = null
    suppressClick.current = false
  }, [])

  // عوض‌کردن ابزار وسطِ کار نباید نقطه‌های ابزار قبلی را با خودش ببرد.
  //
  // بدون این، یک کلیک با «خط روند» و بعد رفتن سراغ «کانال»، کانالی می‌ساخت
  // که رأس اولش نقطه‌ی خط روند بود — و کاربر می‌دید ابزار «عجیب» رفتار
  // می‌کند بی‌آنکه بفهمد چرا.
  useEffect(() => { resetDraft() }, [tool, resetDraft])

  /* ── ساخت ترسیم تازه (ابزارهای کلیکی) */
  const commitDraft = (pts: Point[], kind: Exclude<Tool, 'none'>) => {
    const d: Drawing = { id: uid(), kind, pts, color }
    if (kind === 'text') {
      const txt = window.prompt('متن یادداشت:')
      if (!txt) { setDraft([]); setGhost(null); onToolDone(); return }
      d.text = txt.slice(0, 120)
    }
    setDraft([])
    setGhost(null)
    onCommit(d)
    // قلم آزاد معمولاً چند خط پشت‌سرهم کشیده می‌شود؛ بستنِ ابزار بعد از هر
    // خط، کار را آزاردهنده می‌کرد. بقیه ابزارها یک‌بارمصرف‌اند.
    if (kind !== 'brush') onToolDone()
  }

  const onPointerDown = (e: React.PointerEvent) => {
    // شروعِ هر ژست تازه، پرچمِ «کلیک را ببلع» را پاک می‌کند.
    //
    // چرا لازم است: ابزار سه‌نقطه‌ای (کانال) بعد از کشیدن، هنوز کامل نشده و
    // ابزار عوض نمی‌شود؛ روی لمس هم مرورگر بعد از کشیدن اصلاً `click`
    // نمی‌فرستد. پس پرچم می‌ماند و نقطه سومِ کاربر بی‌صدا بلعیده می‌شد.
    suppressClick.current = false
    // انگشت دومِ کاربر (تلاش برای بزرگ‌نمایی) نباید خطِ در حال کشیدن را از
    // نو شروع کند، و کلیک راست هم نباید ترسیم بسازد.
    if (!e.isPrimary) return
    if (e.pointerType === 'mouse' && e.button !== 0) return
    if (tool === 'none') return
    const pt = fromEvent(e)
    if (!pt) return
    ;(e.currentTarget as Element).setPointerCapture?.(e.pointerId)
    if (FREEHAND.includes(tool)) {
      freehand.current = [pt]
      lastPx.current = { x: e.clientX, y: e.clientY }
      setDraft([pt])
      return
    }
    // بقیه ابزارها هم «کشیدنی» می‌شوند: نگه‌دار، بکش، رها کن.
    press.current = { pt, x: e.clientX, y: e.clientY }
  }

  const onPointerUp = (e: React.PointerEvent) => {
    if (!e.isPrimary) return
    // ── قلم آزاد
    const fh = freehand.current
    freehand.current = null
    lastPx.current = null
    if (fh) {
      if (fh.length < 2) { setDraft([]); setGhost(null); return }
      // ساده‌سازی در فضای پیکسل: نقطه‌هایی که روی همان خط افتاده‌اند حذف
      // می‌شوند. شکل خط عوض نمی‌شود ولی حجم ذخیره‌سازی چند برابر کم می‌شود.
      const px = fh.map((q) => ({ x: x(q.t), y: y(q.p) }))
      // اگر حتی یک نقطه مختصات ندارد (بیرون از دید)، ساده‌سازی را رها
      // می‌کنیم: صفر گذاشتن به‌جای مقدارِ نامعلوم، الگوریتم را گمراه می‌کند
      // و دقیقاً بدترین نقطه‌ها را نگه می‌دارد.
      const ok = px.every((o) => o.x != null && o.y != null)
      const kept = ok
        ? simplifyIndices(px as { x: number; y: number }[], 1.2).map((i) => fh[i])
        : fh
      commitDraft(kept.length >= 2 ? kept : fh, 'brush')
      return
    }

    // ── «کشیدن برای ترسیم»
    //
    // پیش از این فقط «کلیک، کلیک» کار می‌کرد و کاربری که مثل هر برنامه
    // نموداری دیگری نگه می‌داشت و می‌کشید، فکر می‌کرد ابزار خراب است. حالا
    // هر دو روش کار می‌کند: کشیدن، یا دو بار کلیک.
    const pr = press.current
    press.current = null
    if (!pr || tool === 'none') return
    const moved = Math.hypot(e.clientX - pr.x, e.clientY - pr.y) > 5
    if (!moved) return                       // تکْ‌کلیک بود؛ `onClick` کارش را می‌کند
    suppressClick.current = true
    const need = TOOL_POINTS[tool]
    if (need === 1) { commitDraft([pr.pt], tool); return }
    const end = fromEvent(e) ?? pr.pt
    const pts = draft.length ? [...draft, end] : [pr.pt, end]
    if (pts.length < need) { setDraft(pts); setGhost(null); return }
    commitDraft(pts.slice(0, need), tool)
  }

  const onClick = (e: React.MouseEvent) => {
    // بیرون‌آمدن از حالت انتخاب، کارِ شنونده کلیکِ خودِ نمودار است (بالاتر)؛
    // در این حالت لایه اصلاً رویداد نمی‌گیرد و اینجا چیزی برای انجام نیست.
    // اگر همین حالا با «کشیدن» ترسیم شد، کلیکِ پس از آن نباید نقطه تازه‌ای
    // بگذارد.
    //
    // ترتیب این دو شرط مهم است: اگر اول از ابزار بیرون می‌رفتیم، پرچم
    // پاک‌نشده می‌ماند و **کلیک بعدیِ کاربر بی‌صدا بلعیده می‌شد** — چون بعد
    // از ترسیم، ابزار به «انتخاب» برمی‌گردد و شرط اول جلوتر می‌افتاد.
    const swallow = suppressClick.current
    suppressClick.current = false
    if (swallow) return
    if (tool === 'none' || FREEHAND.includes(tool)) return
    const pt = fromEvent(e)
    if (!pt) return
    const need = TOOL_POINTS[tool]
    const pts = [...draft, pt]
    if (pts.length < need) { setDraft(pts); return }
    commitDraft(pts, tool)
  }

  const applyDrag = useCallback((e: { clientX: number; clientY: number }) => {
    const g = drag.current
    if (!g) return
    const pt = fromEvent(e)
    if (!pt) return
    const d = items.find((i) => i.id === g.id)
    if (!d) return
    const dt = pt.t - g.at.t
    const dp = pt.p - g.at.p
    const pts = g.idx === 'all'
      ? g.from.map((q) => ({ t: q.t + dt, p: q.p + dp }))
      : g.from.map((q, i) => (i === g.idx ? pt : q))
    onUpdate({ ...d, pts })
  }, [items, fromEvent, onUpdate])

  const onMove = (e: React.PointerEvent) => {
    if (!e.isPrimary) return
    if (drag.current) return                       // جابه‌جایی را پنجره می‌گیرد
    if (press.current || draft.length) {
      if (!freehand.current) { setGhost(fromEvent(e)); return }
    }
    if (freehand.current) {
      // نقطه‌ای که کمتر از دو پیکسل با قبلی فاصله دارد، شکل خط را عوض
      // نمی‌کند و فقط حافظه و حجم ذخیره‌سازی می‌خورد.
      const l = lastPx.current
      if (l && Math.hypot(e.clientX - l.x, e.clientY - l.y) < 2) return
      const pt = fromEvent(e)
      if (!pt) return
      lastPx.current = { x: e.clientX, y: e.clientY }
      if (freehand.current.length < MAX_BRUSH_POINTS) {
        freehand.current.push(pt)
        setDraft([...freehand.current])
      }
      return
    }
  }

  // در حین جابه‌جایی، رویداد را از **پنجره** می‌گیریم نه از خودِ لایه.
  //
  // چرا: وقتی ابزاری فعال نیست لایه `pointer-events: none` است تا نمودار
  // مثل همیشه حرکت کند. در آن حالت فقط خودِ خطِ نازکِ ترسیم رویداد می‌گیرد و
  // به‌محض اینکه نشانگر یک پیکسل از آن فاصله می‌گرفت، جابه‌جایی نصفه رها
  // می‌شد. شنیدن از پنجره این مشکل را کامل برمی‌دارد.
  useLayoutEffect(() => {
    if (!dragging) return
    const mv = (e: PointerEvent) => applyDrag(e)
    const up = () => { drag.current = null; setDragging(false); redraw() }
    window.addEventListener('pointermove', mv)
    window.addEventListener('pointerup', up)
    window.addEventListener('pointercancel', up)
    return () => {
      window.removeEventListener('pointermove', mv)
      window.removeEventListener('pointerup', up)
      window.removeEventListener('pointercancel', up)
    }
  }, [dragging, applyDrag, redraw])

  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        resetDraft()
        drag.current = null
        setDragging(false)
        onToolDone()
      }
    }
    window.addEventListener('keydown', key)
    return () => window.removeEventListener('keydown', key)
  }, [onToolDone, resetDraft])

  /* ── رسم */
  const { w, h } = size()
  const shapes = useMemo(() => {
    const out: JSX.Element[] = []
    if (hidden) return out

    // لنگرها یک بار برای کل رسم گرفته می‌شوند، نه به ازای هر نقطه: یک قلمِ
    // سیصد‌نقطه‌ای وگرنه ششصد بار از کتابخانه مختصات می‌پرسید — در هر فریمِ
    // جابه‌جایی نمودار.
    const an0 = anchors()
    const xa = (t: number): number | null => {
      if (!an0 || !times.length) return x(t)
      const lg = logicalFromTime(times, t, step)
      return lg == null ? x(t) : an0.c0 + lg * an0.per
    }
    // پیش‌نمایشِ در حال کشیدن.
    //
    // دو حالت دارد: یا کاربر کلیک‌کلیک می‌کند (نقطه‌ها در `draft`اند)، یا
    // نگه داشته و می‌کشد (نقطه اول در `press` است و هنوز وارد `draft`
    // نشده). هر دو باید همان‌طور که کاربر می‌کشد دیده شوند.
    const startPts = draft.length ? draft
      : (press.current && !freehand.current ? [press.current.pt] : [])
    const drafting = (startPts.length > 0) && tool !== 'none'
    const draftPts = freehand.current ? draft : (ghost ? [...startPts, ghost] : startPts)
    // ابزار سه‌نقطه‌ای با دو نقطه هنوز کانال نیست، ولی کاربر باید **خطِ
    // پایه** را ببیند؛ وگرنه روی لمس (که نشانگرِ شناور ندارد) بین رها کردن
    // و ضربه آخر، هیچ چیزی روی صفحه نبود و ابزار خراب به نظر می‌رسید.
    const previewKind: Tool = tool === 'channel' && draftPts.length === 2 ? 'trend' : tool
    const enough = freehand.current ? 2 : TOOL_POINTS[previewKind as Exclude<Tool, 'none'>]
    const all = drafting && draftPts.length >= enough
      ? [...items, { id: '__draft', kind: previewKind, pts: draftPts, color } as Drawing]
      : items

    for (const d of all) {
      // دومین سد در برابر ترسیمِ ناقص: حتی اگر چیزی از پالایش رد شده باشد،
      // نباید هنگام رسم خطا بدهد و صفحه را سفید کند.
      if (!d.pts?.length || d.pts.length < (TOOL_POINTS[d.kind] ?? 1)) continue
      const isSel = d.id === selected
      const pick = (i: number) => {
        const px = xa(d.pts[i].t)
        const py = y(d.pts[i].p)
        return px == null || py == null ? null : { px, py }
      }
      const stroke = d.color
      const sw = isSel ? 2.5 : 1.5
      const interactive = tool === 'none' && !locked && d.id !== '__draft'
      const hit = (extra: Record<string, any> = {}) => ({
        onPointerDown: (e: React.PointerEvent) => {
          if (!interactive) return
          e.stopPropagation()
          onSelect(d.id)
          const at = fromEvent(e)
          if (at) { drag.current = { id: d.id, idx: 'all', from: d.pts, at }; setDragging(true) }
        },
        onClick: (e: React.MouseEvent) => e.stopPropagation(),
        // `touchAction: none` لازم است: بدون آن، انگشتی که روی خط می‌کشد را
        // مرورگر «اسکرول صفحه» می‌فهمد و با اولین پیکسل حرکت، رویداد را با
        // `pointercancel` پس می‌گیرد؛ یعنی جابه‌جایی ترسیم روی گوشی کار
        // نمی‌کرد.
        style: { pointerEvents: (interactive ? 'stroke' : 'none') as any,
                 cursor: 'move', touchAction: 'none' } as any,
        ...extra,
      })

      // نوارِ نامرئیِ پهن برای گرفتن کلیک/لمس.
      //
      // خطِ دیده‌شده ۱٫۵ پیکسل است؛ با انگشت عملاً نمی‌شود گرفتش و کاربر
      // فکر می‌کند «ترسیم انتخاب نمی‌شود». پس زیر هر خط، یک خطِ شفافِ
      // چهارده‌پیکسلی می‌گذاریم که رویداد را می‌گیرد.
      // نوارِ گرفتن: باریک برای خط‌های سرتاسری (وگرنه هر جابه‌جاییِ نمودار
      // که نزدیک یک سطح شروع شود، به‌جای حرکت‌دادن نمودار، خط را می‌گیرد) و
      // پهن‌تر وقتی همان ترسیم انتخاب شده است.
      const GRAB = isSel ? 16 : 12
      const GRAB_FULL = isSel || coarse ? 16 : 8

      if (d.kind === 'hline') {
        const py = y(d.pts[0].p)
        if (py == null) continue
        out.push(
          <g key={d.id}>
            <line x1={0} x2={w} y1={py} y2={py} stroke="transparent" strokeWidth={GRAB_FULL}
              {...hit()} />
            <line x1={0} x2={w} y1={py} y2={py} stroke={stroke} strokeWidth={sw}
              strokeDasharray="6 4" style={{ pointerEvents: 'none' }} />
            <rect x={w - 92} y={py - 9} width={86} height={18} rx={4} fill={stroke} opacity={0.9} />
            <text x={w - 49} y={py + 4} fontSize="10" textAnchor="middle" fill="#06070C">
              {fmt(d.pts[0].p)}
            </text>
          </g>,
        )
        continue
      }

      if (d.kind === 'vline') {
        const px = xa(d.pts[0].t)
        if (px == null) continue
        out.push(
          <g key={d.id}>
            <line x1={px} x2={px} y1={0} y2={h} stroke="transparent" strokeWidth={GRAB_FULL}
              {...hit()} />
            <line x1={px} x2={px} y1={0} y2={h} stroke={stroke} strokeWidth={sw}
              strokeDasharray="6 4" style={{ pointerEvents: 'none' }} />
          </g>,
        )
        continue
      }

      if (d.kind === 'brush') {
        const pts = d.pts.map((_, i) => pick(i)).filter(Boolean) as { px: number; py: number }[]
        if (pts.length < 2) continue
        const poly = pts.map((q) => `${q.px},${q.py}`).join(' ')
        out.push(
          <g key={d.id}>
            <polyline fill="none" stroke="transparent" strokeWidth={GRAB}
              strokeLinejoin="round" strokeLinecap="round" points={poly} {...hit()} />
            <polyline fill="none" stroke={stroke} strokeWidth={sw + 0.5}
              strokeLinejoin="round" strokeLinecap="round" points={poly}
              style={{ pointerEvents: 'none' }} />
          </g>,
        )
        // دستگیره‌ها فقط در حالت انتخاب فعال‌اند.
        //
        // اگر با ابزارِ فعال هم زنده می‌ماندند، خطِ تازه‌ای که درست کنار
        // انتهای خط قبلی شروع می‌شد، به‌جای ترسیم، **خط قبلی را جابه‌جا
        // می‌کرد** — چون قلم آزاد پس از هر خط انتخاب می‌شود و ابزارش هم باز
        // می‌ماند.
        if (isSel && interactive) {
          out.push(...handles(d, pick, stroke, [0, d.pts.length - 1], startDrag))
        }
        continue
      }

      const a = pick(0)
      const b = d.pts.length > 1 ? pick(1) : null
      if (!a || (d.pts.length > 1 && !b)) continue

      if (d.kind === 'text') {
        // ناحیه شفافِ پشتِ متن، وگرنه فقط خطِ دورِ حروف قابل گرفتن بود
        const tw = Math.max(40, (d.text?.length ?? 0) * 7)
        out.push(
          <g key={d.id}>
            <rect x={a.px - tw / 2} y={a.py - 26} width={tw} height={34} fill="transparent"
              {...hit({ style: { pointerEvents: interactive ? 'all' : 'none',
                                 cursor: 'move', touchAction: 'none' } as any })} />
            <circle cx={a.px} cy={a.py} r={4} fill={stroke} style={{ pointerEvents: 'none' }} />
            <text x={a.px} y={a.py - 10} fontSize="11" fill={stroke} textAnchor="middle"
              style={{ paintOrder: 'stroke', stroke: '#06070C', strokeWidth: 3,
                       pointerEvents: 'none' }}>
              {d.text}
            </text>
          </g>,
        )
      } else if (d.kind === 'trend' || d.kind === 'ray' || d.kind === 'ext') {
        const seg = d.kind === 'trend' ? { s: a, e: b! }
          : d.kind === 'ray' ? { s: a, e: extend(a, b!, w) }
            : { s: extend(b!, a, w), e: extend(a, b!, w) }
        out.push(
          <g key={d.id}>
            <line x1={seg.s.px} y1={seg.s.py} x2={seg.e.px} y2={seg.e.py}
              stroke="transparent" strokeWidth={GRAB} {...hit()} />
            <line x1={seg.s.px} y1={seg.s.py} x2={seg.e.px} y2={seg.e.py}
              stroke={stroke} strokeWidth={sw} style={{ pointerEvents: 'none' }} />
          </g>,
        )
      } else if (d.kind === 'arrow') {
        const head = arrowHead(a, b!)
        out.push(
          <g key={d.id}>
            <line x1={a.px} y1={a.py} x2={b!.px} y2={b!.py}
              stroke="transparent" strokeWidth={GRAB} {...hit()} />
            <line x1={a.px} y1={a.py} x2={b!.px} y2={b!.py} stroke={stroke}
              strokeWidth={sw + 0.5} style={{ pointerEvents: 'none' }} />
            <polygon points={head} fill={stroke} style={{ pointerEvents: 'none' }} />
          </g>,
        )
      } else if (d.kind === 'channel' && d.pts.length > 2) {
        const c = pick(2)
        if (!c) continue
        // فاصله عمودیِ نقطه سوم از خط پایه، همان پهنای کانال است
        const dy = c.py - lineY(a, b!, c.px)
        const a2 = { px: a.px, py: a.py + dy }
        const b2 = { px: b!.px, py: b!.py + dy }
        out.push(
          <g key={d.id} {...hit({ style: { pointerEvents: interactive ? 'all' : 'none',
                                            cursor: 'move', touchAction: 'none' } as any })}>
            <polygon points={`${a.px},${a.py} ${b!.px},${b!.py} ${b2.px},${b2.py} ${a2.px},${a2.py}`}
              fill={stroke} fillOpacity={0.1} />
            <line x1={a.px} y1={a.py} x2={b!.px} y2={b!.py} stroke={stroke} strokeWidth={sw} />
            <line x1={a2.px} y1={a2.py} x2={b2.px} y2={b2.py} stroke={stroke} strokeWidth={sw} />
          </g>,
        )
      } else if (d.kind === 'rect' && b) {
        out.push(
          <rect key={d.id} x={Math.min(a.px, b.px)} y={Math.min(a.py, b.py)}
            width={Math.abs(b.px - a.px)} height={Math.abs(b.py - a.py)}
            fill={stroke} fillOpacity={0.12} stroke={stroke} strokeWidth={sw}
            {...hit({ style: { pointerEvents: interactive ? 'all' : 'none', cursor: 'move',
                               touchAction: 'none' } as any })} />,
        )
      } else if (d.kind === 'measure' && b) {
        const up = d.pts[1].p >= d.pts[0].p
        const dp = d.pts[1].p - d.pts[0].p
        const pctv = d.pts[0].p ? (dp / d.pts[0].p) * 100 : 0
        const nbars = step ? Math.round(Math.abs(d.pts[1].t - d.pts[0].t) / step) : 0
        const cx = (a.px + b.px) / 2
        const col = up ? '#00E39A' : '#FF4D6D'
        out.push(
          <g key={d.id} {...hit({ style: { pointerEvents: interactive ? 'all' : 'none',
                                            cursor: 'move', touchAction: 'none' } as any })}>
            <rect x={Math.min(a.px, b.px)} y={Math.min(a.py, b.py)}
              width={Math.abs(b.px - a.px)} height={Math.abs(b.py - a.py)}
              fill={col} fillOpacity={0.1} stroke={col} strokeWidth={1} strokeDasharray="4 3" />
            <line x1={cx} y1={a.py} x2={cx} y2={b.py} stroke={col} strokeWidth={1.5} />
            <polygon points={arrowHead({ px: cx, py: a.py }, { px: cx, py: b.py })} fill={col} />
            <rect x={cx - 62} y={Math.min(a.py, b.py) - 24} width={124} height={20} rx={5}
              fill={col} opacity={0.92} />
            <text x={cx} y={Math.min(a.py, b.py) - 10} fontSize="10" textAnchor="middle"
              fill="#06070C">
              {`${fmt(dp)} (${pctv.toFixed(2)}%) · ${nbars} کندل`}
            </text>
          </g>,
        )
      } else if (d.kind === 'fib' && b) {
        const p0 = d.pts[0].p
        const p1 = d.pts[1].p
        out.push(
          <g key={d.id}>
            <rect x={Math.min(a.px, b.px)} y={Math.min(a.py, b.py)}
              width={Math.abs(b.px - a.px)} height={Math.abs(b.py - a.py)}
              fill="transparent" {...hit({ style: { pointerEvents: interactive ? 'all' : 'none', cursor: 'move', touchAction: 'none' } as any })} />
            {FIB_LEVELS.map((lv) => {
              const price = p1 + (p0 - p1) * lv
              const py = y(price)
              if (py == null) return null
              return (
                <g key={lv}>
                  <line x1={Math.min(a.px, b.px)} x2={Math.max(a.px, b.px)} y1={py} y2={py}
                    stroke={stroke} strokeWidth={1} strokeOpacity={0.75} strokeDasharray="4 3" />
                  <text x={Math.min(a.px, b.px) + 4} y={py - 3} fontSize="9" fill={stroke}>
                    {(lv * 100).toFixed(1)}٪ · {fmt(price)}
                  </text>
                </g>
              )
            })}
          </g>,
        )
      }

      // دستگیره‌ها فقط روی ترسیم انتخاب‌شده
      if (isSel && interactive) {
        out.push(...handles(d, pick, stroke, d.pts.map((_, i) => i), startDrag))
      }
    }
    return out

    function startDrag(id: string, idx: number, pts: Point[], e: React.PointerEvent) {
      const at = fromEvent(e)
      if (at) { drag.current = { id, idx, from: pts, at }; setDragging(true) }
    }
  }, [items, draft, ghost, selected, tool, color, w, h, x, y, fromEvent, onSelect, tick,
      hidden, locked, step, times, anchors, coarse])

  return (
    <div
      ref={host}
      onClick={onClick}
      onPointerMove={onMove}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onPointerCancel={resetDraft}
      className="absolute inset-0"
      style={{
        // وقتی ابزاری فعال نیست، لایه شفاف است تا نمودار مثل همیشه
        // جابه‌جا و بزرگ‌نمایی شود. تک‌تک ترسیم‌ها خودشان رویداد می‌گیرند.
        pointerEvents: tool === 'none' ? 'none' : 'auto',
        cursor: tool === 'none' ? 'default' : 'crosshair',
        touchAction: tool === 'none' ? 'auto' : 'none',
      }}
    >
      <svg width={w} height={h} style={{ overflow: 'hidden' }}>{shapes}</svg>
    </div>
  )
}

/** دستگیره‌های جابه‌جایی نقطه‌ها */
function handles(
  d: Drawing,
  pick: (i: number) => { px: number; py: number } | null,
  stroke: string,
  idxs: number[],
  start: (id: string, idx: number, pts: Point[], e: React.PointerEvent) => void,
) {
  const out: JSX.Element[] = []
  for (const i of idxs) {
    const q = pick(i)
    if (!q) continue
    out.push(
      <circle key={`${d.id}-h${i}`} cx={q.px} cy={q.py} r={6}
        fill="#06070C" stroke={stroke} strokeWidth={2}
        style={{ pointerEvents: 'all', cursor: 'grab', touchAction: 'none' }}
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => { e.stopPropagation(); start(d.id, i, d.pts, e) }} />,
    )
  }
  return out
}

type Px = { px: number; py: number }

/** امتداد پاره‌خط a→b تا لبه نمودار */
/**
 * `memo` عمدی است: شمارش معکوسِ بسته‌شدن کندل هر ثانیه کارت را دوباره رندر
 * می‌کند و بدون این، لایه ترسیم هم هر ثانیه از نو ساخته می‌شد — با یک
 * `getBoundingClientRect` اجباری در هر بار.
 */
export const DrawLayer = memo(DrawLayerImpl)

function extend(a: Px, b: Px, w: number): Px {
  const dx = b.px - a.px
  if (Math.abs(dx) < 0.5) return { px: b.px, py: b.py }
  const slope = (b.py - a.py) / dx
  const far = dx > 0 ? w + 40 : -40
  return { px: far, py: a.py + slope * (far - a.px) }
}

/** ارتفاع خط a→b در یک x مشخص */
function lineY(a: Px, b: Px, px: number): number {
  const dx = b.px - a.px
  if (Math.abs(dx) < 0.5) return a.py
  return a.py + ((b.py - a.py) / dx) * (px - a.px)
}

/** سه‌گوشِ نوک پیکان روی نقطه b */
function arrowHead(a: Px, b: Px, size = 9): string {
  const ang = Math.atan2(b.py - a.py, b.px - a.px)
  const p = (t: number, r: number) =>
    `${b.px - Math.cos(ang + t) * r},${b.py - Math.sin(ang + t) * r}`
  return `${b.px},${b.py} ${p(0.4, size)} ${p(-0.4, size)}`
}

function fmt(v: number) {
  if (!Number.isFinite(v)) return '—'
  const abs = Math.abs(v)
  const d = abs >= 1000 ? 0 : abs >= 1 ? 2 : 6
  return v.toLocaleString('en-US', { maximumFractionDigits: d })
}
